<template>
  <article
    class="pcard"
    :class="{
      'pcard--preview': isPreview,
      [`pcard--hl-${activeZone}`]: isPreview && activeZone,
    }"
  >
    <!-- 몰 목록용: 상세로 이동 -->
    <router-link
      v-if="!isPreview"
      :to="`/mall/${product.id}`"
      class="pcard__link"
      :aria-label="`${product.name}, ${isGroupBuy ? '1개당 ' : ''}${formatPrice(priced.unitPrice)}원`"
    >
      <div class="pcard__media">
        <img
          class="pcard__img"
          :src="product.image"
          :alt="`${product.name} 상품 이미지`"
          loading="lazy"
          decoding="async"
        />
        <span v-if="product.badge" class="pcard__badge" :class="badgeClass">{{ product.badge }}</span>
        <MallOrderPackMark
          v-if="isPack && !isGroupBuy"
          size="card"
          :order-step="product.orderStep || 1"
          :sell-unit="product.sellUnit"
        />
        <span v-if="product.soldOut" class="pcard__soldout">품절</span>
      </div>

      <div class="pcard__info">
        <p class="pcard__meta">
          <span class="pcard__rating">
            <AppIcon icon="mdi-star" :size="14" />
            {{ product.rating.toFixed(1) }}
          </span>
          <span class="pcard__sold">{{ formatSold(product.soldCount) }}개 구매</span>
        </p>
        <h3 class="pcard__name">{{ product.name }}</h3>

        <div class="pcard__price-row">
          <span class="pcard__amount"><small v-if="isGroupBuy">1개당</small>{{ formatPrice(priced.unitPrice) }}<em>원</em><small v-if="!isGroupBuy && sellNoun === '박스'">/박스</small></span>
        </div>

        <div class="pcard__bottom">
          <span class="pcard__review">구매후기 {{ formatSold(product.reviewCount) }}</span>
          <span
            class="pcard__stock"
            :class="{ 'is-out': product.soldOut || !(stockQty > 0) }"
          >
            {{ stockLabel }}
          </span>
        </div>
      </div>
    </router-link>

    <!-- 상품 편집 미리보기: 요소 클릭 → 해당 입력칸 -->
    <div v-else class="pcard__link pcard__link--preview" aria-label="몰 목록 카드 미리보기">
      <button
        type="button"
        class="pcard__hit pcard__media"
        :class="{ 'is-on': zoneOn('gallery') || zoneOn('badge') }"
        title="이미지 · 배지 수정"
        @click="emitEdit('gallery')"
      >
        <span class="pcard__hit-label">이미지 · 배지</span>
        <img
          v-if="product.image"
          class="pcard__img"
          :src="product.image"
          :alt="`${product.name} 상품 이미지`"
        />
        <span v-else class="pcard__img-empty">이미지 없음</span>
        <span
          v-if="product.badge"
          class="pcard__badge"
          :class="[badgeClass, { 'is-on': zoneOn('badge') }]"
          @click.stop="emitEdit('badge')"
        >
          {{ product.badge }}
        </span>
        <MallOrderPackMark
          v-if="isPack && !isGroupBuy"
          size="card"
          :order-step="product.orderStep || 1"
          :sell-unit="product.sellUnit"
        />
        <span v-if="product.soldOut" class="pcard__soldout">품절</span>
      </button>

      <div class="pcard__info">
        <p class="pcard__meta pcard__meta--muted" title="판매·평점은 주문·후기 후 반영됩니다">
          <span class="pcard__rating">
            <AppIcon icon="mdi-star" :size="14" />
            {{ product.rating.toFixed(1) }}
          </span>
          <span class="pcard__sold">{{ formatSold(product.soldCount) }}개 구매</span>
        </p>

        <button
          type="button"
          class="pcard__hit pcard__hit--block"
          :class="{ 'is-on': zoneOn('name') }"
          title="상품명 수정"
          @click="emitEdit('name')"
        >
          <span class="pcard__hit-label">상품명</span>
          <h3 class="pcard__name">{{ product.name || '상품명 없음' }}</h3>
        </button>

        <button
          type="button"
          class="pcard__hit pcard__hit--block"
          :class="{ 'is-on': zoneOn('price') }"
          title="가격 수정"
          @click="emitEdit('price')"
        >
          <span class="pcard__hit-label">가격</span>
          <div class="pcard__price-row">
            <span class="pcard__amount"><small v-if="isGroupBuy">1개당</small>{{ formatPrice(priced.unitPrice) }}<em>원</em><small v-if="!isGroupBuy && sellNoun === '박스'">/박스</small></span>
          </div>
        </button>

        <div class="pcard__bottom">
          <span class="pcard__review pcard__meta--muted" title="후기는 구매 후 쌓입니다">
            구매후기 {{ formatSold(product.reviewCount) }}
          </span>
          <button
            type="button"
            class="pcard__hit pcard__stock"
            :class="{
              'is-out': product.soldOut || !(stockQty > 0),
              'is-on': zoneOn('stock'),
            }"
            title="재고 수정"
            @click="emitEdit('stock')"
          >
            <span class="pcard__hit-label">재고</span>
            {{ stockLabel }}
          </button>
        </div>
      </div>
    </div>

    <div class="pcard__actions" :class="{ 'is-preview': isPreview }">
      <button
        type="button"
        class="pcard__tool"
        :class="{ 'is-on': wished }"
        :aria-label="wished ? '찜 해제' : '찜하기'"
        :disabled="isPreview"
        @click="onWish"
      >
        <AppIcon :icon="wished ? 'mdi-heart' : 'mdi-heart-outline'" :size="18" />
      </button>
      <button
        type="button"
        class="pcard__tool"
        aria-label="공유하기"
        :disabled="isPreview"
        @click="onShare"
      >
        <AppIcon icon="mdi-share-variant-outline" :size="18" />
      </button>
      <button
        type="button"
        class="pcard__cart"
        :disabled="isPreview || product.soldOut || cartBusy"
        @click="onCart"
      >
        <AppIcon icon="mdi-cart-outline" :size="16" />
        {{ product.soldOut ? '품절' : cartBusy ? '담는중' : '장바구니' }}
      </button>
    </div>
  </article>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import AppIcon from '@/components/common/AppIcon.vue'
import MallOrderPackMark from '@/components/mall/MallOrderPackMark.vue'
import { type MallProduct, formatPrice } from '@/constants/mallProducts'
import type { ProductPreviewZone } from '@/constants/productEditor'
import { useCart } from '@/composables/useCart'
import { useMallAuth } from '@/composables/useMallAuth'
import { useMallToast } from '@/composables/useMallToast'
import { useWishlist } from '@/composables/useWishlist'
import { calcMallUnitPrice } from '@/utils/mallPricing'
import { formatStockCount, isOrderPack, sellUnitNoun } from '@/utils/mallSell'
import { shareMallProduct } from '@/utils/mallShare'

const props = withDefaults(
  defineProps<{
    product: MallProduct
    /** 상품 편집 미리보기 — 클릭 시 입력칸으로 포커스 */
    mode?: 'default' | 'preview'
    activeZone?: ProductPreviewZone | ''
  }>(),
  {
    mode: 'default',
    activeZone: '',
  },
)

const emit = defineEmits<{
  (event: 'edit-zone', zone: ProductPreviewZone, fieldSuffix?: string): void
}>()

const isPreview = computed(() => props.mode === 'preview')

const router = useRouter()
const { isWished, toggleWish } = useWishlist()
const { addItem } = useCart()
const { ensureLoggedIn } = useMallAuth()
const { showSuccess, showError } = useMallToast()
const cartBusy = ref(false)

function zoneOn(zone: ProductPreviewZone): boolean {
  return props.activeZone === zone
}

function emitEdit(zone: ProductPreviewZone, fieldSuffix?: string) {
  emit('edit-zone', zone, fieldSuffix)
}

const onWish = () => {
  if (isPreview.value) return
  if (!ensureLoggedIn()) return
  toggleWish(props.product.id)
}

const onCart = async () => {
  if (isPreview.value) return
  if (!ensureLoggedIn() || props.product.soldOut || cartBusy.value) return
  if ((props.product.options || []).length) {
    await router.push(`/mall/${props.product.id}`)
    return
  }
  cartBusy.value = true
  try {
    await addItem(props.product.id, Math.max(1, props.product.minOrder || 1))
    showSuccess('장바구니에 담았습니다.')
  } catch (error) {
    showError(error)
  } finally {
    cartBusy.value = false
  }
}

const onShare = async () => {
  if (isPreview.value) return
  const url = `${window.location.origin}/mall/${props.product.id}`
  const result = await shareMallProduct({
    title: props.product.name,
    text: `${props.product.name} — 함우리몰`,
    url,
  })
  if (result === 'copied') showSuccess('상품 주소를 복사했습니다.')
}

const priced = computed(() =>
  calcMallUnitPrice({
    listPrice: props.product.listPrice,
    salePrice: props.product.price,
  }),
)

const wished = computed(() => isWished(props.product.id))
const stockQty = computed(() => Math.max(0, Number(props.product.stock) || 0))
const sellNoun = computed(() => sellUnitNoun(props.product.sellUnit))
const isGroupBuy = computed(() => props.product.mallKind === 'SALE')
const isPack = computed(() => isOrderPack(props.product.orderStep))
const stockLabel = computed(() => {
  if (props.product.soldOut || !(stockQty.value > 0)) return '품절'
  return `재고 ${formatStockCount(stockQty.value, props.product.sellUnit)}`
})

const badgeClass = computed(() => {
  if (props.product.badge === '특가') return 'pcard__badge--urgent'
  if (props.product.badge === 'NEW') return 'pcard__badge--new'
  if (props.product.badge === '베스트') return 'pcard__badge--best'
  return 'pcard__badge--hot'
})

function formatSold(value: number) {
  if (value >= 10000) return `${(value / 10000).toFixed(1)}만`
  return value.toLocaleString('ko-KR')
}
</script>

<style lang="scss" src="./ProductCard.scss"></style>
